import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TabContext, TabPanel } from '@mui/lab';
import { Fade, Stack, Typography } from '@mui/material';

import { ILessonHistory } from '../../../../../../../../store/V2/LessonHistorySlice';
import TabBar, {
  ITabItem,
} from '../../../../../../../../components/V2/Tabs/type2/TabBar';
import { ISection } from '../../../../../../../../store/V2/SectionSlice';
import BasicLoader from '../../../../../../../../components/Loaders/BasicLoader';
import {
  addNewStudentFeedbackData,
  getLessonStudentFeedbacks,
  getLessonStudentFeedbacksState,
  ILessonStudentFeedback,
  resetLessonStudentFeedbackListData,
  TGetLessonStudentFeedbacks,
  updateStudentFeedbackData,
} from '../../../../../../../../store/V2/LessonStudentFeedbacksSlice';
import { useAppSelector } from '../../../../../../../../hooks';
import {
  addNewTutorFeedbackData,
  getLessonTutorFeedbacks,
  getLessonTutorFeedbacksState,
  ILessonTutorFeedback,
  resetLessonTutorFeedbackListData,
  TGetLessonTutorFeedbacks,
  updateTutorFeedbackData,
} from '../../../../../../../../store/V2/LessonTutorFeedbacksSlice';
import { IDENTIFIED_USER_ROLES } from '../../../../../../../../store/UserSlice';
import { getAuthState } from '../../../../../../../../store/AuthSlice';
import ViewStudentFeedbackForm from '../../../../../../../../components/V2/Feedback/ViewStudentFeedbackForm';
import AddStudentFeedbackForm from '../../../../../../../../components/V2/Feedback/AddStudentFeedbackForm';
import AddTutorFeedbackForm from '../../../../../../../../components/V2/Feedback/AddTutorFeedbackForm';
import ViewTutorFeedbackForm from '../../../../../../../../components/V2/Feedback/ViewTutorFeedbackForm';
import GroupClassStudentFeedbackContentsForTutor from '../../../../../../../../components/V2/Feedback/GroupClassStudentFeedbackContentsForTutor';
import GroupClassTutorFeedbackContentsForStudent from '../../../../../../../../components/V2/Feedback/GroupClassTutorFeedbackContentsForStudent';
import {
  IFeedbackStudent,
  IFeedbackTutor,
} from '../../../../../../../../components/V2/Feedback/helper';
import GroupClassStudentFeedbackContentsForStudent from '../../../../../../../../components/V2/Feedback/GroupClassStudentFeedbackContentsForStudent';
import GroupClassTutorFeedbackContentsForTutor from '../../../../../../../../components/V2/Feedback/GroupClassTutorFeedbackContentsForTutor';

interface FeedbacksTabProps {
  lessonHistory: ILessonHistory;
}

type TState = {
  selectedTab: ITabItem;
  tabOptions: ITabItem[];
};

const defautlTabs: ITabItem[] = [
  {
    index: 0,
    key: 'studentFeedbacks',
    title: "Student's Feedback",
  },
  {
    index: 1,
    key: 'tutorFeedbacks',
    title: "Tutor's Feedback",
  },
];

const initialState: TState = {
  selectedTab: defautlTabs[0],
  tabOptions: [...defautlTabs],
};

const FeedbacksTab: FunctionComponent<FeedbacksTabProps> = ({
  lessonHistory,
}) => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const lessonStudentFeedbacksState = useAppSelector(
    getLessonStudentFeedbacksState
  );

  const lessonTutorFeedbacksState = useAppSelector(
    getLessonTutorFeedbacksState
  );

  // State
  const [state, setState] = useState<TState>({ ...initialState });
  const [initialLoading, setInitialLoading] = useState(false);
  const [tabsSetupCompleted, setTabsSetupCompleted] = useState(false);

  const authUser = authState.user;
  const sectionData = lessonHistory.section as ISection;
  const studentList = lessonHistory.students || [];
  const tutorList = lessonHistory.tutors || [];
  const studentFeedbacks =
    lessonStudentFeedbacksState.lessonStudentFeedbackListData &&
    lessonStudentFeedbacksState.lessonStudentFeedbackListData.list &&
    lessonStudentFeedbacksState.lessonStudentFeedbackListData.list.length
      ? lessonStudentFeedbacksState.lessonStudentFeedbackListData.list
      : [];

  const tutorFeedbacks =
    lessonTutorFeedbacksState.lessonTutorFeedbackListData &&
    lessonTutorFeedbacksState.lessonTutorFeedbackListData.list &&
    lessonTutorFeedbacksState.lessonTutorFeedbackListData.list.length
      ? lessonTutorFeedbacksState.lessonTutorFeedbackListData.list
      : [];

  const feedbackStudents: IFeedbackStudent[] = studentList.map((item) => ({
    _id: item._id,
    firstname: item.firstname,
    lastname: item.lastname,
  }));

  const feedbackTutors: IFeedbackTutor[] = tutorList.map((item) => ({
    _id: item._id,
    firstname: item.firstname,
    lastname: item.lastname,
  }));

  useEffect(() => {
    initialize();

    return () => {
      dispatch(resetLessonStudentFeedbackListData());
      dispatch(resetLessonTutorFeedbackListData());
    };
  }, []);

  useEffect(() => {
    if (!tabsSetupCompleted) {
      setState((prevState) => {
        const newStateData = { ...prevState };

        if (lessonHistory.identifiedUserRole) {
          if (
            lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT
          ) {
            const newTabOptions = [
              {
                index: 0,
                key: 'studentFeedbacks',
                title: 'Rate your tutor',
              },
              {
                index: 1,
                key: 'tutorFeedbacks',
                title: `Feedback${
                  tutorFeedbacks.length > 1 ? 's' : ''
                } from tutor${tutorFeedbacks.length > 1 ? 's' : ''}`,
              },
            ];

            newStateData.tabOptions = newTabOptions;

            // eslint-disable-next-line prefer-destructuring
            newStateData.selectedTab = newTabOptions[0];
          } else if (
            lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR
          ) {
            const newTabOptions = [
              {
                index: 0,
                key: 'studentFeedbacks',
                title: `Feedback${
                  studentFeedbacks.length > 1 ? 's' : ''
                } from student${studentFeedbacks.length > 1 ? 's' : ''}`,
              },
              {
                index: 1,
                key: 'tutorFeedbacks',
                title: 'Rate your student',
              },
            ];

            newStateData.tabOptions = newTabOptions;

            // eslint-disable-next-line prefer-destructuring
            newStateData.selectedTab = newTabOptions[0];
          }
        }

        return newStateData;
      });

      setTabsSetupCompleted(true);
    }
  }, [
    lessonStudentFeedbacksState.lessonStudentFeedbackListData,
    lessonTutorFeedbacksState.lessonTutorFeedbackListData,
  ]);

  const initialize = async () => {
    setInitialLoading(true);

    if (lessonHistory.identifiedUserRole && authUser) {
      const studentFeedbacksApiData: TGetLessonStudentFeedbacks = {
        limit: 200,
        offset: 0,
        section: sectionData._id,
      };

      const tutorFeedbacksApiData: TGetLessonTutorFeedbacks = {
        limit: 200,
        offset: 0,
        section: sectionData._id,
      };

      if (lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT) {
        studentFeedbacksApiData.studentId = authUser._id;
        tutorFeedbacksApiData.studentId = authUser._id;
      } else if (
        lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR
      ) {
        tutorFeedbacksApiData.tutorId = authUser._id;
        studentFeedbacksApiData.tutorId = authUser._id;
      }

      await dispatch(getLessonStudentFeedbacks(studentFeedbacksApiData));
      await dispatch(getLessonTutorFeedbacks(tutorFeedbacksApiData));
    }

    setInitialLoading(false);
  };

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const onStudentFeedbackAdded = (feedbackData: ILessonStudentFeedback[]) => {
    dispatch(addNewStudentFeedbackData(feedbackData));
  };

  const onStudentFeedbackUpdated = (
    id: string,
    feedbackData: ILessonStudentFeedback
  ) => {
    dispatch(
      updateStudentFeedbackData({
        id,
        feedback: feedbackData,
      })
    );
  };

  const onTutorFeedbackAdded = (feedbackData: ILessonTutorFeedback[]) => {
    dispatch(addNewTutorFeedbackData(feedbackData));
  };

  const onTutorFeedbackUpdated = (
    id: string,
    feedbackData: ILessonTutorFeedback
  ) => {
    dispatch(
      updateTutorFeedbackData({
        id,
        feedback: feedbackData,
      })
    );
  };

  const renderStudentFeedbackContents = () => {
    if (sectionData && sectionData._id && lessonHistory.identifiedUserRole) {
      if (lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT) {
        if (feedbackTutors.length > 1) {
          return (
            <Fade in>
              <div style={{ padding: '16px 0' }}>
                <GroupClassStudentFeedbackContentsForStudent
                  feedbacks={studentFeedbacks}
                  tutors={feedbackTutors}
                  sectionId={sectionData._id}
                  onFeedbackAdded={onStudentFeedbackAdded}
                  onFeedbackUpdated={onStudentFeedbackUpdated}
                />
              </div>
            </Fade>
          );
        }

        const feedbackTutor = feedbackTutors[0];
        const studentFeedback = studentFeedbacks[0];

        return (
          <Fade in>
            <div style={{ padding: '16px 0' }}>
              <AddStudentFeedbackForm
                sectionId={sectionData._id}
                onFeedbackAdded={onStudentFeedbackAdded}
                onFeedbackUpdated={onStudentFeedbackUpdated}
                feedback={studentFeedback}
                tutors={[feedbackTutor]}
              />
            </div>
          </Fade>
        );
        // eslint-disable-next-line no-else-return
      } else if (
        lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR
      ) {
        if (studentFeedbacks.length) {
          if (studentFeedbacks.length === 1) {
            const studentFeedback = studentFeedbacks[0];

            return (
              <Fade in>
                <div style={{ padding: '16px 0' }}>
                  <ViewStudentFeedbackForm feedback={studentFeedback} />
                </div>
              </Fade>
            );
          }

          return (
            <Fade in>
              <div style={{ padding: '16px 0' }}>
                <GroupClassStudentFeedbackContentsForTutor
                  feedbacks={studentFeedbacks}
                />
              </div>
            </Fade>
          );
        }

        return (
          <Fade in>
            <div style={{ padding: '16px 0' }}>
              <Typography>No feedback yet</Typography>
            </div>
          </Fade>
        );
      }
    }

    return null;
  };

  const renderTutorFeedbackContents = () => {
    if (sectionData && sectionData._id && lessonHistory.identifiedUserRole) {
      if (lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.TUTOR) {
        if (feedbackStudents.length > 1) {
          return (
            <Fade in>
              <div style={{ padding: '16px 0' }}>
                <GroupClassTutorFeedbackContentsForTutor
                  feedbacks={tutorFeedbacks}
                  students={feedbackStudents}
                  sectionId={sectionData._id}
                  onFeedbackAdded={onTutorFeedbackAdded}
                  onFeedbackUpdated={onTutorFeedbackUpdated}
                />
              </div>
            </Fade>
          );
        }

        const feedbackStudent = feedbackStudents[0];
        const tutorFeedback = tutorFeedbacks[0];

        return (
          <Fade in>
            <div style={{ padding: '16px 0' }}>
              <AddTutorFeedbackForm
                sectionId={sectionData._id}
                feedback={tutorFeedback}
                onFeedbackAdded={onTutorFeedbackAdded}
                onFeedbackUpdated={onTutorFeedbackUpdated}
                students={[feedbackStudent]}
              />
            </div>
          </Fade>
        );
        // eslint-disable-next-line no-else-return
      } else if (
        lessonHistory.identifiedUserRole === IDENTIFIED_USER_ROLES.STUDENT
      ) {
        if (tutorFeedbacks.length) {
          if (tutorFeedbacks.length === 1) {
            const tutorFeedback = tutorFeedbacks[0];

            return (
              <Fade in>
                <div style={{ padding: '16px 0' }}>
                  <ViewTutorFeedbackForm feedback={tutorFeedback} />
                </div>
              </Fade>
            );
          }

          return (
            <Fade in>
              <div style={{ padding: '16px 0' }}>
                <GroupClassTutorFeedbackContentsForStudent
                  feedbacks={tutorFeedbacks}
                />
              </div>
            </Fade>
          );
        }

        return (
          <Fade in>
            <div style={{ padding: '16px 0' }}>
              <Typography>No feedback yet</Typography>
            </div>
          </Fade>
        );
      }
    }

    return null;
  };

  const renderTabPanel = () => {
    let tabPanel: JSX.Element | null = null;

    const panelProps = {
      value: state.selectedTab.index.toString(),
      sx: { p: 0 },
    };

    switch (state.selectedTab.index) {
      case 0:
        tabPanel = (
          <TabPanel {...panelProps}>{renderStudentFeedbackContents()}</TabPanel>
        );
        break;

      case 1:
        tabPanel = (
          <TabPanel {...panelProps}>{renderTutorFeedbackContents()}</TabPanel>
        );
        break;

      default:
        tabPanel = null;
        break;
    }

    return tabPanel;
  };

  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Feedbacks
          </Typography>
        </div>
        {initialLoading ? (
          <Stack sx={{ p: '30px 0' }}>
            <BasicLoader />
          </Stack>
        ) : (
          <div>
            <TabContext value={state.selectedTab.index.toString()}>
              <div style={{ borderBottom: '2px solid #1E293B14' }}>
                <div style={{ padding: '0 10px' }}>
                  <TabBar
                    selectedTab={state.selectedTab}
                    tabs={state.tabOptions}
                    onChange={(e, value) => {
                      updateStateAttributes({
                        selectedTab: state.tabOptions[value],
                      });
                    }}
                  />
                </div>
              </div>
              <div>{renderTabPanel()}</div>
            </TabContext>
          </div>
        )}
      </div>
    </Fade>
  );
};

export default FeedbacksTab;
